import Sentry from '@sentry/browser';
import { videoCreateFinishParams } from '@wix/bi-logger-vmaf/v2/types';
import { videoCreateStart, videoCreateFinish } from '@wix/bi-logger-vmaf/v2';
import { ActionType } from 'typesafe-actions';
import * as api from '../../api/videoMaker';
import biLogger from '../../lib/bi/biLogger';
import { Thunk } from '../rootReducer';
import { getVideoType } from '../videoInfo/selectors';

import { prepareVideoInput } from '../videoInput/selectors';
import { getVideoStyleOptions } from '../videoStyle/operations';
import {
  requestVideoOutput,
  setVideoOutputNeedRetry,
  storeVideoMetaId,
  storeVideoMetaSessionId,
} from './actions';
import { getVideoOutputStartTime, getVideoSessionId } from './selectors';

interface IVideoCreateBIData {
  params: Partial<videoCreateFinishParams>;
  startTime: number;
}

function logVideoOutputError(videoCreateBI: IVideoCreateBIData, error: any) {
  void biLogger.report(
    videoCreateFinish({
      ...videoCreateBI.params,
      creationTime: Date.now() - videoCreateBI.startTime,
      success: false,
      errorReason: JSON.stringify(error),
    }),
  );
}

function createVideoOutput(): Thunk<
  ActionType<typeof requestVideoOutput | typeof storeVideoMetaSessionId>
> {
  return async (dispatch, getState) => {
    const state = getState();
    const startTime = Date.now();
    const videoCreateBI: IVideoCreateBIData = {
      params: {
        type: getVideoType(state),
        videoType: 'final',
      },
      startTime,
    };
    void biLogger.report(videoCreateStart(videoCreateBI.params));
    dispatch(requestVideoOutput.request(startTime));

    try {
      const videoStyleOptions = await dispatch(getVideoStyleOptions());
      const videoInput = prepareVideoInput(state, {
        videoStyleOptions,
      });

      // NOTE: video not created yet. This request only started job
      const job = await api.requestCreatingVideo(videoInput);

      dispatch(storeVideoMetaSessionId(job.sessionId));
    } catch (error) {
      dispatch(requestVideoOutput.failure(error));
      void logVideoOutputError(videoCreateBI, error);

      Sentry.captureException(error);
    }
  };
}

function waitForVideoOutput(): Thunk<
  ActionType<typeof requestVideoOutput | typeof storeVideoMetaId>
> {
  return (dispatch, getState) => {
    const state = getState();
    const videoSessionId = getVideoSessionId(state);

    const videoCreateBI: IVideoCreateBIData = {
      params: {
        type: getVideoType(state),
        videoType: 'final',
        videoCreationSessionId: String(videoSessionId),
      },
      startTime: getVideoOutputStartTime(state),
    };

    api
      .waitForVideoCreation(videoSessionId)
      .then((data) => {
        dispatch(storeVideoMetaId(data.resultVideoId));
        dispatch(
          requestVideoOutput.success({
            downloadUrl: data.downloadUrl,
            videoUrl: data.previewUrl,
            thumbnailUrl: data.thumbUrl,
          }),
        );

        void biLogger.report(
          videoCreateFinish({
            ...videoCreateBI.params,
            videoId: data.resultVideoId,
            videoUrl: data.previewUrl,
            creationTime: Date.now() - videoCreateBI.startTime,
            success: true,
          }),
        );
      })
      .catch((error) => {
        dispatch(requestVideoOutput.failure(error));

        void logVideoOutputError(videoCreateBI, error);

        Sentry.captureException(error);
      });
  };
}

export { createVideoOutput, setVideoOutputNeedRetry, waitForVideoOutput };
